import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as dailyReportActions from '@/components/dailyReport/Actions';
import * as errorHandlerActions from '@/components/errorHandler/Actions';
import * as learningPathActions from '@/pages/learningPath/Actions';
import * as sharedActions from '@/shared/Actions';
import {
	FRONTEND_ROUTES,
	getExperimentPath,
	getUpdateUserPath,
	IS_ADMIN,
	IS_LOCAL_ENVIRONMENT,
	LOGOUT_REDIRECT_URL,
	MARKETING_URL,
	ROUTES_API,
} from '@/services/Constants';
import { sharedTypes } from '@/shared/Constants';
import { identifyHotjar } from '@/services/Helper';
import { deleteExperiment, handleExperiment, isLearningPath } from '@/shared/Helpers';
import { getUserId } from '@/features/onboarding';
import { removeAccessToken } from '@/services/AccessToken';
import { processRequest } from '@/services/Api';
import { historyReplace } from '@/services/History';

import type { SagaIterator } from 'redux-saga';
import type { DailyReportData } from '@/components/dailyReport/types';
import type { UserData } from '@/shared/types';

type SagaAction = {
	type: string;
	payload?: Record<string, unknown>;
};

interface GetUserDataOptions {
	skipOnboardingCheck?: boolean;
}

interface ExperimentResponse {
	experiment?: {
		name?: string;
		random_variation_weights?: number[] | null;
	} | null;
}

export default function* (): Generator {
	yield all([
		takeLatest(sharedTypes.GET_USER_DATA_REQUEST, handleGetUserDataRequest),
		takeLatest(sharedTypes.LOGOUT, handleUserLogout),
		takeLatest(sharedTypes.UPDATE_USER_DATA_REQUEST, handleUpdateUserDataRequest),
		takeLatest(sharedTypes.SEND_EVENT_REQUEST, handleSendEventRequest),
		takeLatest(sharedTypes.HANDLE_USER_EXPERIMENT, handleUserExperimentRequest),
	]);
}

export function* handleGetUserDataRequest({ payload }: SagaAction): SagaIterator {
	try {
		const options = payload?.options as GetUserDataOptions | undefined;

		const { data: userData } = (yield call(processRequest, ROUTES_API.GET_USER_DATA)) as {
			data: UserData;
		};

		const {
			needs_to_fill_grade,
			child_name,
			grade_id,
			sidk_user,
			sidk_closed,
			show_autonomy_experiment,
			play_mode,
		} = (userData || {}) as UserData & {
			show_autonomy_experiment?: boolean;
		};

		if (sidk_user && sidk_closed) {
			yield put(sharedActions.logout());
			window.location.href = `${MARKETING_URL}school-gesloten`;
			return;
		}

		yield put(sharedActions.getUserDataSuccess(userData));

		if (IS_ADMIN) return;

		identifyHotjar(userData);

		if (options?.skipOnboardingCheck) return;

		if (!grade_id) {
			yield call(historyReplace, FRONTEND_ROUTES.ONBOARDING_CHOOSE_GRADE);
			return;
		}

		if (needs_to_fill_grade) {
			yield call(historyReplace, FRONTEND_ROUTES.NEW_SCHOOL_YEAR);
		}

		if (!child_name) {
			yield call(historyReplace, FRONTEND_ROUTES.SAVE_CHILD_NAME);
			return;
		}

		if (show_autonomy_experiment) {
			yield put(learningPathActions.setAutonomyMode(true));
		}

		const { data: dailyReport } = (yield call(processRequest, ROUTES_API.DAILY_REPORT)) as {
			data: DailyReportData;
		};
		yield put(dailyReportActions.getDailyReportSuccess(dailyReport));

		identifyHotjar(userData, dailyReport);

		if (isLearningPath(play_mode)) {
			yield put(learningPathActions.getLearningPathRequest());
		}
	} catch (e) {
		yield put(sharedActions.getUserDataError());
		yield put(errorHandlerActions.handleError(e, 'page-blocking'));
	}
}

export function* handleUserLogout(): SagaIterator {
	try {
		if (IS_ADMIN || IS_LOCAL_ENVIRONMENT) {
			yield call(processRequest, ROUTES_API.INVALIDATE_TOKEN, 'POST');
			yield call(removeAccessToken);
			window.location.href = FRONTEND_ROUTES.LOGIN;
		} else {
			window.location.href = LOGOUT_REDIRECT_URL;
		}
	} catch (e) {
		yield put(sharedActions.getUserDataError());
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

export function* handleUpdateUserDataRequest({ payload }: SagaAction): SagaIterator {
	try {
		const userId = (yield select(getUserId)) as string | number;
		const { data } = payload || {};

		yield call(processRequest, getUpdateUserPath(String(userId)), 'PUT', {
			user: data,
		});

		const { data: userData } = (yield call(processRequest, ROUTES_API.GET_USER_DATA)) as {
			data: UserData;
		};

		yield put(sharedActions.getUserDataSuccess(userData));

		yield put(sharedActions.updateUserDataSuccess());
	} catch (e) {
		yield put(sharedActions.updateUserDataError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleSendEventRequest({ payload }: SagaAction): SagaIterator {
	try {
		const requestData = payload?.data;
		if (IS_LOCAL_ENVIRONMENT) {
			console.log('send_event', requestData);
		} else {
			yield call(
				processRequest,
				ROUTES_API.SEND_EVENT,
				'POST',
				requestData as Record<string, any> | undefined,
			);
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(sharedActions.sendEventError());
	}
}

export function* handleUserExperimentRequest({ payload }: SagaAction): SagaIterator {
	const experimentName = payload?.experimentName as string;

	try {
		const { data } = (yield call(processRequest, getExperimentPath(experimentName))) as {
			data: ExperimentResponse;
		};
		if (data.experiment) {
			yield call(handleExperiment, data.experiment);
		}
		yield put(sharedActions.handleUserExperimentSuccess(experimentName));
	} catch (e) {
		yield call(deleteExperiment, experimentName);
		yield put(sharedActions.handleUserExperimentSuccess(experimentName));
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}
